import { useQuery } from '@tanstack/react-query';
import { API } from '@/utils/API';
import { StudySiteData } from '@/types/study';

export interface SitesPagination {
  total: number;
  page?: number;
  per_page?: number;
  total_pages?: number;
}

export interface UseSitesListParams {
  search?: string;
  status?: string;
  order?: string;
  sort?: string;
  studyId?: string;
  unattached?: boolean;
  /** Defaults to true. Set to false to skip fetching (e.g. while a modal is closed). */
  enabled?: boolean;
  /** 1-indexed. Omit (with perPage) to fetch every matching row, as today. */
  page?: number;
  perPage?: number;
}

interface SitesResponse {
  resources: StudySiteData[];
  pagination: SitesPagination;
  /** Distinct, sorted countries across the search/status-filtered set (not
   * just the current page) — computed once alongside `resources` so the
   * country-filter dropdown doesn't need its own separate request. */
  countries: string[];
}

export const buildSitesSearchParams = (
  params: UseSitesListParams
): URLSearchParams => {
  const searchParams = new URLSearchParams({
    order: params.order || 'desc',
    sort: params.sort || 'created_at',
    search: params.search || '',
    status: params.status || '',
  });

  if (params.studyId) {
    searchParams.set('study_id', params.studyId);
  }

  if (params.unattached) {
    searchParams.set('unattached', 'true');
  }

  if (params.page) {
    searchParams.set('page', String(params.page));
    searchParams.set('per_page', String(params.perPage || 25));
  }

  return searchParams;
};

export const fetchSites = async (
  params: UseSitesListParams
): Promise<SitesResponse> => {
  const searchParams = buildSitesSearchParams(params);

  const response = await API.authenticated.fetch(
    `/sites?${searchParams.toString()}`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch sites');
  }

  return response.json();
};

// `data` stays the plain resources array — the shape every existing consumer
// (Studies/Show.tsx's site list, ConnectSiteModal, ScopedSitesTable) already
// expects — with `pagination` surfaced alongside it for callers (the Sites
// index page) that opt into `page`/`perPage` and need the total page count.
export const useSitesList = ({
  enabled = true,
  ...params
}: UseSitesListParams) => {
  const query = useQuery({
    queryKey: ['sites', params],
    queryFn: () => fetchSites(params),
    enabled,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });

  return {
    ...query,
    data: query.data?.resources,
    pagination: query.data?.pagination,
    countries: query.data?.countries,
  };
};
